/* eslint-disable no-tabs */
/* eslint-disable no-mixed-spaces-and-tabs
 */
import { Grid, Paper, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TabContext, TabPanel } from '@mui/lab';
import clsx from 'clsx';
import { AddAndUpdatePartyLedgerEntry } from 'components/add-and-update-party-ledger-modal/add-and-update-party-ledger-entry';
import AddEntry from 'components/add-entry-modal/add-entry-modal';
import { CustomTab as Tab } from 'components/common/custom-tab';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import React, { FC, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { capitalize } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';

import Header from './components/header';
import ModalLinkentry from './components/modal-link-entry/modal-link-entry';
import SankeyDiagram from './components/sankeyDiagram';
import Ledger from './components/table-ledger/table-ledger';
import { TableLedgerItemColumn } from './services';
import AccountingContext from './services/context';

const useStyles = makeStyles((theme: any) => ({
  noinformation: {
    display: 'flex',
    justifyItems: 'center',
  },
  noinformationFont: {
    color: theme.palette.common.black,
    fontWeight: 500,
    textAlign: 'center',
  },
  padding: { padding: theme.spacing(3) },
  tabs: {
    '& .MuiTabScrollButton-root': {
      width: theme.spacing(4),
      flexShrink: 0,
      [theme.breakpoints.down('md')]: {
        width: theme.spacing(3.5),
      },
    },
  },
}));

type PendingDelete = {
  title: string;
  message: string;
  action: () => void;
};

type AccountingScenetype = {
  accountingData: AccountingType[];
  handleUpdateLedger?: (acc: AccountingType[]) => void;
  handleDeleteLedgerItem: (item: TableLedgerItemColumn) => void;
  handleDeleteLedgerParty: (item: any) => void;
  t: TFunction;
  resolver: any;
  handledeleteLink: (
    order_id: string,
    paymentRecordId: string,
    associationKind: string,
    associationId: string,
    associationLedgerRow: string
  ) => void;
  state: any;
  dispatch: React.Dispatch<any>;
  handleReviseOrderLedger: () => void;
  loadingRevise: boolean;
};

const AccountingScene: FC<AccountingScenetype> = ({
  accountingData,
  handleUpdateLedger,
  t,
  handleDeleteLedgerItem,
  handleDeleteLedgerParty,
  resolver,
  handledeleteLink,
  state,
  dispatch,
  handleReviseOrderLedger,
  loadingRevise
}) => {
  const [currentTab, setcurrentTab] = useState('money-flow');

  const handleChange = (_: any, index: string) => {
    setcurrentTab(index);
  };

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const kindList = useMemo(
    () =>
      accountingData.reduce<string[] | undefined>((prev, acc) => {
        if (prev && !prev.includes(acc.kind)) {
          return [...prev!, acc.kind];
        }
        return prev;
      }, []) || [],
    [accountingData]
  );

  const handleAddParty = (row: any) => {
    dispatch({
      type: 'add-association',
      entryLedger: row,
      associationSelected: null,
    });
  };

  const handleEditAmount = (row: any) => {
    dispatch({
      type: 'add-entry',
      entryLedger: row,
      associationSelected: null,
    });
  };

  const handleEditLedgerAssociation = (row: any) => {
    dispatch({
      type: 'add-association',
      entryLedger: row.ledgerInfo,
      associationSelected: row.row,
    });
  };

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const requestDelete = (action: () => void) => {
    setPendingDelete({
      title: t('accounting:confirmDialog:deleteTitle', { defaultValue: 'Confirm Delete' }),
      message: t('accounting:confirmDialog:deleteMessage', {
        defaultValue: 'This action cannot be undone.',
      }),
      action,
    });
  };

  const handleDeleteLedgetEntry = (row: any) => {
    requestDelete(() => handleDeleteLedgerItem(row));
  };

  const handleDeleteAssociation = (item: any) => {
    requestDelete(() => handleDeleteLedgerParty(item));
  };

  const handleDeleteLink = (
    order_id: string,
    paymentRecordId: string,
    associationKind: string,
    associationId: string,
    associationLedgerRow: string
  ) => {
    requestDelete(() => handledeleteLink(
      order_id,
      paymentRecordId,
      associationKind,
      associationId,
      associationLedgerRow
    ));
  };

  const handleCreationLink = (row: any) => {
    dispatch({
      type: 'link',
      entryLedger: row,
      associationSelected: null,
    });
  };

  return (
    <>
      <AccountingContext.Provider
        value={{
          handleAddParty,
          handleDeleteLedgetEntry,
          handleEditLedgerAssociation,
          handleEditAmount,
          handleDeleteLegerAssociation: handleDeleteAssociation,
          handledeleteLink: handleDeleteLink,
          handleCreateLink: handleCreationLink,
        }}
      >
        <Grid className={classes.padding}>
          <Paper>
            <Header
              title={t('accounting:accounting')}
              handleAddEntry={() => {
                dispatch({
                  type: 'add-entry',
                  associationSelected: null,
                  entryLedger: null,
                });
              }}
              handleReviseOrderLedger={handleReviseOrderLedger}
              loadingRevise={loadingRevise}
            />

            <TabContext value={currentTab}>
              {accountingData.length === 0 && (
              <Grid item xs={12} className={clsx(classes.noinformation, classes.padding)}>
                <Typography className={classes.noinformationFont}>
                  {t('accounting:void-ledger')}
                </Typography>
              </Grid>
              )}
              <Grid item>
                <Tabs
                  variant="scrollable"
                  scrollButtons
                  className={classes.tabs}
                  value={currentTab}
                  onChange={handleChange}
                  allowScrollButtonsMobile
                >
                  <Tab
                    key="money-flow"
                    currentValue={currentTab}
                    value="money-flow"
                    text={isMobile ? t('accounting:money-flow-short') : t('accounting:money-flow')}
                  />
                  <Tab
                    key="ledger"
                    currentValue={currentTab}
                    value="ledger"
                    text={t('accounting:ledger')}
                  />
                  {kindList.sort((a, b) => {
                    return a.localeCompare(b);
                  }).map((kind) => (
                    <Tab
                      key={kind}
                      currentValue={currentTab}
                      value={kind}
                      text={capitalize(kind.replace(/_/g, ' '))}
                    />
                  ))}
                </Tabs>
              </Grid>
              <Grid item xs={12}>
                <TabPanel style={{ padding: 24 }} value="money-flow">
                  <SankeyDiagram accounting={accountingData} />
                </TabPanel>
                <TabPanel style={{ padding: 24 }} value="ledger">
                  <Ledger ledgerData={accountingData} />
                </TabPanel>
                {kindList.map((kind) => (
                  <TabPanel className={classes.padding} value={kind} key={kind}>
                    <Ledger ledgerData={accountingData.filter((acc) => acc.kind.includes(kind))} />
                  </TabPanel>
                ))}
              </Grid>
            </TabContext>
          </Paper>
        </Grid>
      </AccountingContext.Provider>
      {state.mode === 'add-entry' && (
      <AddEntry
        open={state.mode === 'add-entry'}
        handleClose={() => {
          dispatch({
            entryLedger: null,
            associationSelected: null,
            mode: null,
          });
        }}
        isEditingEntry={state.entryLedger !== null}
        prevData={
          state.entryLedger !== null
            ? {
              ...state.entryLedger,
              _id: state.entryLedger.id,
              entry_date: state.entryLedger.entryDate,
            }
            : null
        }
        resolver={resolver}
        hideFields={
          state.entryLedger !== null
            ? {
              code: true,
              amount: true,
              kind: true,
            }
            : { amount_override: true }
        }
      />
      )}
      {state.mode === 'add-association' && (
      <AddAndUpdatePartyLedgerEntry
        handleClose={() => {
          dispatch({
            entryLedger: null,
            associationSelected: null,
            mode: null,
          });
        }}
        entryLedger={
          state.entryLedger !== null
            ? {
              ...state.entryLedger,
              _id: state.entryLedger.id,
              entry_date: state.entryLedger.entryDate,
            }
            : ({} as AccountingType)
        }
        partyLedgerEntry={state.associationSelected}
      />
      )}
      {state.mode === 'link' && (
      <ModalLinkentry
        ledgerData={accountingData}
        currentLedgerEntry={
          state.entryLedger !== null
            ? {
              ...state.entryLedger,
              _id: state.entryLedger.id,
              entry_date: state.entryLedger.entryDate,
            }
            : ({} as AccountingType)
        }
        handleUpdateAccounting={handleUpdateLedger!}
        open={state.mode === 'link'}
        onClose={() => {
          dispatch({
            entryLedger: null,
            associationSelected: null,
            mode: null,
          });
        }}
      />
      )}
      <DeleteConfirmationModal
        title={pendingDelete?.title || ''}
        confirmationMessage={pendingDelete?.message || ''}
        isOpen={!!pendingDelete}
        onConfirm={() => {
          pendingDelete?.action();
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
        isRemoveLoading={false}
        buttonConfirmText={t('common:delete')}
      />
    </>
  );
};

export default AccountingScene;
